import {
  BoundingSphere,
  CallbackPositionProperty,
  CallbackProperty,
  Cartesian2,
  Cartesian3,
  Cesium3DTile,
  Cesium3DTileFeature,
  Cesium3DTileset,
  Math as CesiumMath,
  ClippingPlane,
  ClippingPlaneCollection,
  Color,
  ColorMaterialProperty,
  ConstantPositionProperty,
  ConstantProperty,
  DebugModelMatrixPrimitive,
  Entity,
  HeadingPitchRoll,
  JulianDate,
  Matrix3,
  Matrix4,
  Plane,
  PointPrimitiveCollection,
  Quaternion,
  Ray,
  ScreenSpaceEventHandler,
  Transforms,
  Viewer,
} from "cesium";
import { CesiumViewerClippingBoxConfig } from "../../CesiumViewerClippingBoxConfig";
import { PointsUtils } from "../utils/PointsUtils";
import { SurfaceUtils } from "../utils/SurfaceUtils";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { ActivatableToolInterface } from "./ActivatableToolInterface";
import { BackgroundableToolInterface } from "./BackgroundableToolInterface";
import {
  compareToolState,
  SerializableToolInterface,
} from "./SerializableToolInterface";
import { VectorUtils } from "../utils/VectorUtils";
import {
  DerivedObservableProperty,
  ObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";
import TilesetPlugin from "./TilesetPlugin";
import AssetsPlugin from "./AssetsPlugin";
import ScreenSpaceEventsPlugin from "./ScreenSpaceEventsPlugin";

type PickedClippingPlane = {
  id: Entity;
};

type PositionEvent = {
  position: Cartesian3;
  screenPosition: Cartesian2;
  object?: Cesium3DTileFeature | Cesium3DTile;
};

type ClippingPlanePositionEvent = {
  position: Cartesian3;
  screenPosition: Cartesian2;
  object?: PickedClippingPlane;
};

export type ClippingMode = "standard" | "advanced";

type Cartesian3Like = Pick<Cartesian3, "x" | "y" | "z">;

type ClippedTileset = {
  tilesetId: string | null;
  tileset: Cesium3DTileset;
  isClippingEnabled: boolean;
};

type RecoverableScene = Viewer["scene"] & {
  defaultView?: unknown;
  view?: unknown;
  frameState?: {
    passes?: {
      pick?: boolean;
      offscreen?: boolean;
    };
  };
  context?: {
    endFrame?: () => void;
  };
};

const CLIPPING_BOX_PREVIEW_COLOR = Color.fromCssColorString("#d8b4fe");

export type ClippingBoxToolSnapshot = {
  version: 1;
  boxOrigin: Cartesian3Like;
  boxCenter: Cartesian3Like;
  clippingDistances: number[];
  rotationDegrees: number;
  activeTilesetIds?: string[];
  deactivatedTilesetIds?: string[];
  clippingMode?: ClippingMode;
};

export function isClippingBoxStateApplied(
  state: ClippingBoxToolSnapshot | undefined,
): boolean {
  return !!state;
}

export default class ClippingBoxToolPlugin
  implements
    AttachablePluginInterface,
    ActivatableToolInterface,
    SerializableToolInterface<ClippingBoxToolSnapshot>,
    BackgroundableToolInterface
{
  config: typeof CesiumViewerClippingBoxConfig;
  private _viewer: Viewer | null = null;
  private _isActive = false;
  private _surfaceUtils: SurfaceUtils;
  private _detachLeftDownHandler: (() => void) | null = null;
  private _detachLeftUpHandler: (() => void) | null = null;
  private _detachMouseMoveHandler: (() => void) | null = null;
  private _pendingMouseMoveScreenPosition: Cartesian2 | null = null;
  private _mouseMoveFrame: number | null = null;
  private _mouseMoveEvent: PositionEvent;
  private _previewOrientation: Quaternion = Quaternion.IDENTITY;
  private _pendingPreviewOrientationUpdate: ReturnType<
    typeof setTimeout
  > | null = null;
  private _lastPreviewOrientationUpdateMs = 0;
  private readonly _previewOrientationThrottleMs = 200;
  private _dragStartCameraInertiaTranslate: number | null = null;

  deactivatedTilesets: Cesium3DTileset[] = [];

  initialModelMatrices: Matrix4[] = [];
  scratchModelMatrices: Matrix4[] = [];
  boundingSphere: BoundingSphere;
  normals: Cartesian3[];
  planes: Plane[];
  influencerIndices: number[][];
  positionIndices: Cartesian3[];
  isSelectingBackface = false;
  isIsolatingFace = false;
  planeEntities: Entity[] = [];
  faceCenters: Cartesian3[];
  clippingDistances: number[];
  rotationDegrees: number = 0;
  boxOrigin: Cartesian3;
  boxCenter: Cartesian3;
  clickTargetDebugPrimitive: PointPrimitiveCollection;
  axisDebugPrimitive: DebugModelMatrixPrimitive;
  boxOriginDebugPointPrimitives: PointPrimitiveCollection;
  boxCenterDebugPointPrimitives: PointPrimitiveCollection;
  faceCenterDebugPointPrimitives: PointPrimitiveCollection;
  initialClippingDistance: number;
  enuRotation: Matrix4 = Matrix4.IDENTITY;
  enuRotationScratchMatrix: Matrix4 = new Matrix4();
  planeScratchCartesian3Instances: Cartesian3[][] = []; // 6 Cartesian3 instances for each plane
  planeScratchHprInstances: HeadingPitchRoll[]; // 1 HeadingPitchRoll instance for each plane
  orientationTransformScratchMatrix3Instances: Matrix3[]; // 1 Matrix3 instance for each plane
  planeFaceCenterScratchCartesian3Instances: Cartesian3[][]; // 3 Cartesian3 instances for each plane
  selectedPlaneEntity: Entity;
  previouslySelectedPlaneEntityId: string | undefined;
  hoveredPlaneEntity: Entity;
  previouslyHoveredEntity: Entity | undefined;
  rasterDelta: Cartesian2;
  dragStartPositionRaster: Cartesian2;
  dragStartPositionWorld: Cartesian3;
  dragVectorWorld: Cartesian3;
  originalDistance: number;
  private _entityCursorPoint: Entity;

  private _recalledActiveTilesetIds: Set<string> | null = null;
  private _recalledDeactivatedTilesetIds = new Set<string>();
  private _assetsUnsubscribe: (() => void) | null = null;
  private _syncTimeout: ReturnType<typeof setTimeout> | null = null;

  private readonly _isInitialized: ObservableProperty<boolean> =
    new ObservableProperty(false);
  private readonly _clippingMode: ObservableProperty<ClippingMode> =
    new ObservableProperty("standard");
  private readonly _clippedTilesets: ObservableProperty<ClippedTileset[]> =
    new ObservableProperty([]);
  /**
   * Standard mode creates a box as soon as the tool opens. It only counts as
   * applied once the user has changed it (or it came from a saved view).
   */
  private readonly _hasUserAdjustedBox: ObservableProperty<boolean> =
    new ObservableProperty(false);
  readonly isApplied = new DerivedObservableProperty(
    () =>
      this._hasUserAdjustedBox.getSnapshot() &&
      isClippingBoxStateApplied(this.capture()),
    [this._isInitialized, this._hasUserAdjustedBox],
  );

  constructor(
    private readonly _tilesetPlugin: TilesetPlugin,
    private readonly _assetsPlugin: AssetsPlugin,
    private readonly _screenSpaceEventsPlugin: ScreenSpaceEventsPlugin,
  ) {
    this.config = CesiumViewerClippingBoxConfig;
  }

  get clippingMode(): ReadonlyObservableProperty<ClippingMode> {
    return this._clippingMode;
  }

  get isInitialized(): ReadonlyObservableProperty<boolean> {
    return this._isInitialized;
  }

  get clippedTilesets(): ReadonlyObservableProperty<ClippedTileset[]> {
    return this._clippedTilesets;
  }

  isActive(): boolean {
    return this._isActive;
  }

  activate(): void {
    return this.setActive(true);
  }

  deactivate(): void {
    return this.setActive(false);
  }

  isStateApplied(state: ClippingBoxToolSnapshot | undefined): boolean {
    return isClippingBoxStateApplied(state);
  }

  isMatchingState(state: ClippingBoxToolSnapshot | undefined): boolean {
    return compareToolState(this.capture(), state, (s) =>
      this.isStateApplied(s),
    );
  }

  capture(): ClippingBoxToolSnapshot | undefined {
    if (
      !this._isInitialized.getSnapshot() ||
      !this.boxOrigin ||
      !this.boxCenter ||
      !this.clippingDistances
    ) {
      return undefined;
    }

    const clippedTilesets = this._clippedTilesets.getSnapshot();
    return {
      version: 1,
      boxOrigin: {
        x: this.boxOrigin.x,
        y: this.boxOrigin.y,
        z: this.boxOrigin.z,
      },
      boxCenter: {
        x: this.boxCenter.x,
        y: this.boxCenter.y,
        z: this.boxCenter.z,
      },
      clippingDistances: [...this.clippingDistances],
      rotationDegrees: this.rotationDegrees,
      activeTilesetIds: clippedTilesets
        .map((clippedTileset) => clippedTileset.tilesetId)
        .filter(Boolean),
      deactivatedTilesetIds: clippedTilesets
        .filter((clippedTileset) => !clippedTileset.isClippingEnabled)
        .map((clippedTileset) => clippedTileset.tilesetId)
        .filter(Boolean),
      clippingMode: this._clippingMode.getSnapshot(),
    };
  }

  restore(state: ClippingBoxToolSnapshot | undefined): void {
    if (!this._viewer || !state || !Object.keys(state).length) return;

    const clippingBoxState = state;
    const boxOrigin = this.toCartesian3(clippingBoxState.boxOrigin);
    const boxCenter = this.toCartesian3(clippingBoxState.boxCenter);
    const clippingDistances = clippingBoxState.clippingDistances;
    if (!boxOrigin || !boxCenter || !Array.isArray(clippingDistances)) return;

    this.destroyClippingBox();

    this._clippingMode.set(clippingBoxState.clippingMode ?? "standard");
    this._recalledActiveTilesetIds = clippingBoxState.activeTilesetIds?.length
      ? new Set(clippingBoxState.activeTilesetIds)
      : null;
    this._recalledDeactivatedTilesetIds = new Set(
      clippingBoxState.deactivatedTilesetIds ?? [],
    );

    this.initializeClippingBox(clippingBoxState.rotationDegrees, boxOrigin);

    this.boxOrigin = boxOrigin;
    this.boxCenter = boxCenter;
    this.rotationDegrees = clippingBoxState.rotationDegrees ?? 0;
    this.clippingDistances = [...clippingDistances];
    this.setEnuRotation();
    this.planeEntities?.forEach((_, index) =>
      this.calculatePlanePositions(index),
    );
    this.applyRecalledTilesetState();
    this._hasUserAdjustedBox.set(true);
    this.setPlaneEntitiesVisible(this._isActive);
    this._viewer.scene.requestRender();
  }

  clear(): void {
    this.destroyClippingBox();
  }

  private toCartesian3(value: Cartesian3Like | null | undefined) {
    if (
      !value ||
      !Number.isFinite(value.x) ||
      !Number.isFinite(value.y) ||
      !Number.isFinite(value.z)
    ) {
      return null;
    }

    return Cartesian3.fromElements(value.x, value.y, value.z);
  }

  private getTilesetId(tileset: Cesium3DTileset): string | null {
    // Not extras.tilesetId: a vector tileset shares that with the tileset it
    // was loaded beside, and clipping treats each loaded tileset separately.
    const state = this._tilesetPlugin
      .getAll()
      .find((loaded) => loaded.tileset === tileset);
    return state?.id ?? null;
  }

  private toClippedTileset(tileset: Cesium3DTileset): ClippedTileset {
    return {
      tilesetId: this.getTilesetId(tileset),
      tileset,
      isClippingEnabled: this.shouldEnableTilesetFromRecalledState(tileset),
    };
  }

  private shouldEnableTilesetFromRecalledState(tileset: Cesium3DTileset) {
    const tilesetId = this.getTilesetId(tileset);
    if (!tilesetId) return true;
    if (this._recalledDeactivatedTilesetIds.has(tilesetId)) return false;
    if (this._recalledActiveTilesetIds) {
      return this._recalledActiveTilesetIds.has(tilesetId);
    }
    return true;
  }

  private applyRecalledTilesetState() {
    const clippedTilesets = this._clippedTilesets.getSnapshot();
    this.deactivatedTilesets = [];

    this._clippedTilesets.set(
      clippedTilesets.map((ct) => {
        const isClippingEnabled = this.shouldEnableTilesetFromRecalledState(
          ct.tileset,
        );
        if (ct.tileset.clippingPlanes) {
          ct.tileset.clippingPlanes.enabled = isClippingEnabled;
        }
        if (!isClippingEnabled) this.deactivatedTilesets.push(ct.tileset);
        return { ...ct, isClippingEnabled };
      }),
    );
  }

  setViewer(viewer: Viewer) {
    this._viewer = viewer;
    this._surfaceUtils = new SurfaceUtils(viewer);
    this._assetsUnsubscribe = this._assetsPlugin.assets.subscribe(() => {
      this.scheduleSyncTilesets();
    });
  }

  detachViewer() {
    if (!this._viewer) return;
    this._assetsUnsubscribe?.();
    this._assetsUnsubscribe = null;
    this.clearSyncTimeout();
    this.cancelPendingMouseMove();
    this.detachInputHandlers();
    this._surfaceUtils = null;
    this.removeEntityCursorPoint();
    this._viewer = null;
  }

  setActive(isToolActive: boolean) {
    if (!this._viewer) return;

    this._isActive = isToolActive;
    if (isToolActive) {
      this.attachInputHandlers();

      if (!this._isInitialized.getSnapshot()) {
        this._clippingMode.set(
          (localStorage.getItem("clippingMode") as ClippingMode) ?? "standard",
        );
        if (this._clippingMode.getSnapshot() === "advanced") {
        } else if (this._clippingMode.getSnapshot() === "standard") {
          this.initializeClippingBox();
        }
      } else {
        this.setPlaneEntitiesVisible(true);
      }
    } else {
      this.cancelPendingMouseMove();
      this.detachInputHandlers();
      this.setPlaneEntitiesVisible(false);
      this.removeEntityCursorPoint();
      if (
        this._isInitialized.getSnapshot() &&
        !this._hasUserAdjustedBox.getSnapshot()
      ) {
        this.destroyClippingBox();
      }
    }
  }

  setClippingMode(clippingMode: ClippingMode) {
    this._isInitialized.set(false);
    this._clippingMode.set(clippingMode);
    localStorage.setItem("clippingMode", clippingMode);

    if (clippingMode === "advanced") {
      this.destroyClippingBox();
    } else if (clippingMode === "standard") {
      this.destroyClippingBox();
      this.initializeClippingBox();
    }
  }

  private attachInputHandlers() {
    this.detachInputHandlers();

    this._detachLeftDownHandler =
      this._screenSpaceEventsPlugin.leftDownEvent.addHandler(
        (event: ScreenSpaceEventHandler.PositionedEvent) => {
          if (!this._viewer || !this._isActive) return;

          // A hover update may already be queued from just before the click.
          // Drop it so it cannot run as the first drag frame with a stale mouse
          // position after selectedPlaneEntity is set below.
          this.cancelPendingMouseMove();

          const needsWorldPosition =
            this._clippingMode.getSnapshot() === "advanced" &&
            !this._isInitialized.getSnapshot();

          if (needsWorldPosition) {
            const ev = this.pick(event.position);
            if (!ev) return;
            this.handleClick(ev);
            return;
          }

          // For dragging an existing clipping face we only need to identify the
          // plane entity under the cursor. Avoid pickFromRay here: on large
          // clipped tilesets that ray pick was the noticeable delay before the
          // drag started.
          const pickedObject = this.pickClippingPlane(event.position);
          this.handleMouseDown({
            position: Cartesian3.ZERO,
            screenPosition: event.position,
            object: pickedObject ?? undefined,
          });
        },
      );

    this._detachLeftUpHandler =
      this._screenSpaceEventsPlugin.leftUpEvent.addHandler(() => {
        if (!this._viewer || !this._isActive) return;
        this.handleMouseUp();
      });

    this._detachMouseMoveHandler =
      this._screenSpaceEventsPlugin.mouseMoveEvent.addHandler(
        (event: ScreenSpaceEventHandler.MotionEvent) => {
          if (!this._viewer || !this._isActive) return;

          if (this.selectedPlaneEntity) {
            this.processMouseMove(event.endPosition);
            return;
          }

          this.scheduleMouseMove(event.endPosition);
        },
      );
  }

  private detachInputHandlers() {
    this._detachLeftDownHandler?.();
    this._detachLeftUpHandler?.();
    this._detachMouseMoveHandler?.();
    this._detachLeftDownHandler = null;
    this._detachLeftUpHandler = null;
    this._detachMouseMoveHandler = null;
  }

  private scheduleMouseMove(screenPosition: Cartesian2) {
    this._pendingMouseMoveScreenPosition = Cartesian2.clone(
      screenPosition,
      this._pendingMouseMoveScreenPosition ?? new Cartesian2(),
    );

    if (this._mouseMoveFrame !== null) return;

    this._mouseMoveFrame = requestAnimationFrame(() => {
      this._mouseMoveFrame = null;
      if (
        !this._viewer ||
        !this._isActive ||
        !this._pendingMouseMoveScreenPosition
      ) {
        return;
      }

      const latestScreenPosition = Cartesian2.clone(
        this._pendingMouseMoveScreenPosition,
      );
      this._pendingMouseMoveScreenPosition = null;
      this.processMouseMove(latestScreenPosition);
    });
  }

  private cancelPendingMouseMove() {
    if (this._mouseMoveFrame !== null) {
      cancelAnimationFrame(this._mouseMoveFrame);
      this._mouseMoveFrame = null;
    }
    this._pendingMouseMoveScreenPosition = null;
  }

  private processMouseMove(screenPosition: Cartesian2) {
    try {
      const needsWorldPosition =
        this._clippingMode.getSnapshot() === "advanced" &&
        !this._isInitialized.getSnapshot();

      if (needsWorldPosition) {
        const position = this.pickPositionForMove(screenPosition);
        if (!position) return;
        this.handleMouseMove({
          position,
          screenPosition,
        });
        return;
      }

      // Dragging and standard clipping-box hover only need the current screen
      // position. Avoid pickFromRay here; doing a ray pick on every drag
      // frame made plane movement lag behind the pointer on heavier scenes.
      this.handleMouseMove({
        position: Cartesian3.ZERO,
        screenPosition,
      });
    } catch (error) {
      console.error(error);
    }
  }

  private pick(windowPosition: Cartesian2): PositionEvent | null {
    const picked = this.pickRaw(windowPosition);
    if (!picked?.position) return null;
    return {
      position: picked.position,
      screenPosition: windowPosition,
      object: picked.object,
    };
  }

  private pickScreenObject(windowPosition: Cartesian2) {
    if (!this._viewer || !windowPosition) return null;
    try {
      return this._viewer.scene.pick(windowPosition);
    } catch {
      this.recoverSceneAfterFailedPick(this._viewer);
      return null;
    }
  }

  private pickClippingPlane(
    windowPosition: Cartesian2,
  ): PickedClippingPlane | null {
    if (!this._viewer || !windowPosition) return null;

    try {
      if (this.isSelectingBackface) {
        const clippingPlanePicks = this._viewer.scene
          .drillPick(windowPosition)
          .filter((p): p is PickedClippingPlane =>
            this.isPickedClippingPlane(p),
          );
        return clippingPlanePicks[1] ?? null;
      }

      const picked = this._viewer.scene.pick(windowPosition);
      return this.isPickedClippingPlane(picked) ? picked : null;
    } catch {
      this.recoverSceneAfterFailedPick(this._viewer);
      return null;
    }
  }

  private isPickedClippingPlane(value: unknown): value is PickedClippingPlane {
    if (!value || typeof value !== "object" || !("id" in value)) return false;

    const id = (value as { id?: Entity }).id;
    return !!id?.name?.includes("clipping-entity");
  }

  private disableCameraInputsForDrag() {
    const controller = this._viewer.scene.screenSpaceCameraController;
    if (this._dragStartCameraInertiaTranslate === null) {
      this._dragStartCameraInertiaTranslate = controller.inertiaTranslate;
    }
    controller.inertiaTranslate = 0;
    controller.enableInputs = false;
  }

  private enableCameraInputsAfterDrag() {
    const controller = this._viewer.scene.screenSpaceCameraController;
    controller.enableInputs = true;

    if (this._dragStartCameraInertiaTranslate === null) return;

    const inertiaTranslate = this._dragStartCameraInertiaTranslate;
    this._dragStartCameraInertiaTranslate = null;

    requestAnimationFrame(() => {
      if (!this._viewer) return;
      this._viewer.scene.screenSpaceCameraController.inertiaTranslate =
        inertiaTranslate;
    });
  }

  private pickPositionForMove(windowPosition: Cartesian2): Cartesian3 | null {
    const viewer = this._viewer;
    if (!viewer || !windowPosition) return null;

    if (viewer.scene.pickPositionSupported) {
      try {
        const position = viewer.scene.pickPosition(windowPosition);
        if (position) return position;
      } catch {
        this.recoverSceneAfterFailedPick(viewer);
      }
    }

    const ray = viewer.camera.getPickRay(windowPosition, new Ray());
    if (!ray) return null;

    try {
      return viewer.scene.globe.pick(ray, viewer.scene);
    } catch {
      return null;
    }
  }

  private pickRaw(windowPosition: Cartesian2): {
    position?: Cartesian3;
    object?: Cesium3DTileFeature | Cesium3DTile;
  } | null {
    const viewer = this._viewer;
    if (!viewer || !windowPosition) return null;

    const ray = viewer.camera.getPickRay(windowPosition, new Ray());
    if (!ray) return null;

    const objectsToExclude = viewer.entities.values?.filter(
      (entity) => entity.properties?.excludeFromPicking,
    );

    let pickedFromRay: {
      position?: Cartesian3;
      object?: Cesium3DTileFeature | Cesium3DTile;
    } | null = null;

    try {
      pickedFromRay = viewer.scene.pickFromRay(ray, objectsToExclude, 0.005);
    } catch {
      // Cesium can throw while its offscreen pick framebuffer is being rebuilt
      // during clipping-box activation. Treat that transient state as a miss and
      // fall back to globe picking instead of surfacing a console error.
      this.recoverSceneAfterFailedPick(viewer);
    }

    if (!pickedFromRay?.object) {
      try {
        return { position: viewer.scene.globe.pick(ray, viewer.scene) };
      } catch {
        return null;
      }
    }

    return { position: pickedFromRay.position, object: pickedFromRay.object };
  }

  private recoverSceneAfterFailedPick(viewer: Viewer) {
    const scene = viewer.scene as RecoverableScene;
    if (scene.defaultView) {
      scene.view = scene.defaultView;
    }
    if (scene.frameState?.passes) {
      scene.frameState.passes.pick = false;
      scene.frameState.passes.offscreen = false;
    }
    try {
      scene.context?.endFrame?.();
    } catch {
      // endFrame is only needed if Cesium failed mid-pick after beginning an
      // offscreen frame. If no frame was active, there is nothing to recover.
    }
  }

  private getAdvancedClippingBoxNormal(ev: PositionEvent): Cartesian3 | null {
    try {
      return this._surfaceUtils.getNormalQuick(ev) ?? null;
    } catch {
      if (this._viewer) this.recoverSceneAfterFailedPick(this._viewer);
      return null;
    }
  }

  private getAdvancedClippingBoxPreviewNormal(
    ev: PositionEvent,
  ): Cartesian3 | null {
    try {
      return this._surfaceUtils.getNormalFromDepthSamples(ev) ?? null;
    } catch {
      return null;
    }
  }

  private handleClick(ev: PositionEvent) {
    if (this._clippingMode.getSnapshot() === "advanced") {
      let azimuthDeg = 0;

      const normal = this.getAdvancedClippingBoxNormal({
        position: ev.position,
        screenPosition: ev.screenPosition,
        object: ev.object,
      });

      if (normal) {
        const enuTransform = Transforms.eastNorthUpToFixedFrame(ev.position);
        const enuNormal = Matrix4.multiplyByPointAsVector(
          Matrix4.inverseTransformation(enuTransform, new Matrix4()),
          normal,
          new Cartesian3(),
        );
        enuNormal.z = 0;
        Cartesian3.normalize(enuNormal, enuNormal);
        const azimuth = Math.atan2(enuNormal.x, enuNormal.y);
        azimuthDeg = CesiumMath.toDegrees(
          azimuth < 0 ? azimuth + 2 * Math.PI : azimuth,
        );
      }

      this.initializeClippingBox(azimuthDeg, ev.position);
      this._hasUserAdjustedBox.set(true);
    }
  }

  private handleMouseDown(ev: ClippingPlanePositionEvent) {
    this.handleMouseHoverExit();
    this.previouslySelectedPlaneEntityId = undefined;

    if (!this.isPickedClippingPlane(ev.object)) return;
    const pickedPlane = ev.object;
    this.disableCameraInputsForDrag();

    const planeIndex = pickedPlane.id.properties?.planeIndex?.getValue();
    if (planeIndex === undefined) return;
    this.selectedPlaneEntity = pickedPlane.id;
    //tricky bit: whenever setting plane color, you have to set outline color too, otherwise clipping box does not clip
    this.selectedPlaneEntity.plane.material = new ColorMaterialProperty(
      this.config.faces.selectedColor,
    );
    this.selectedPlaneEntity.plane.outlineColor = new ColorMaterialProperty(
      this.config.faces.outlineColor,
    );

    this.dragStartPositionRaster = ev.screenPosition;
    this.originalDistance = this.clippingDistances[planeIndex];

    const camera = this._viewer.scene.camera;

    /**
     * Calculate the plane direction and scale in screen space,
     * and the normal to the plane in world space.
     */
    const faceCenterOffset = Cartesian3.multiplyByScalar(
      this.planes[planeIndex].normal,
      -this.planes[planeIndex].distance,
      new Cartesian3(),
    );
    const faceCenter = Matrix4.multiplyByPoint(
      this.enuRotation,
      faceCenterOffset,
      new Cartesian3(),
    );
    const normalDirection = new Cartesian3();
    Cartesian3.negate(
      Matrix4.multiplyByPointAsVector(
        this.enuRotation,
        this.planes[planeIndex].normal,
        normalDirection,
      ),
      normalDirection,
    );

    // The camera has an "up" value (+y) and "direction" value (-z). We also need "right" (+x).
    const cameraRight = Cartesian3.cross(
      camera.direction,
      camera.up,
      new Cartesian3(),
    );

    // The direction of the normal to the plane in raster space.
    const rasterDelta = new Cartesian2(
      Cartesian3.dot(normalDirection, cameraRight),
      Cartesian3.dot(normalDirection, camera.up),
    );

    // To work out the scale, pick a point in front of the camera and work out how far
    // the distance is in raster space.
    const pickRay = camera.getPickRay(ev.screenPosition);

    // Intersect the pick ray with the plane
    let distanceToObjective = Cartesian3.distance(
      this.boxOrigin,
      pickRay.origin,
    );
    const rayPlaneDenom = Cartesian3.dot(pickRay.direction, normalDirection);
    if (Math.abs(rayPlaneDenom) > 1e-6) {
      let rayPlaneNumber =
        Cartesian3.dot(normalDirection, faceCenter) -
        Cartesian3.dot(normalDirection, pickRay.origin);
      distanceToObjective = rayPlaneNumber / rayPlaneDenom;
    }
    this.dragStartPositionWorld = Cartesian3.add(
      pickRay.origin,
      Cartesian3.multiplyByScalar(
        pickRay.direction,
        distanceToObjective,
        new Cartesian3(),
      ),
      new Cartesian3(),
    );
    this.dragVectorWorld = normalDirection;
    const viewCentreRaster = this._viewer.scene.cartesianToCanvasCoordinates(
      this.dragStartPositionWorld,
    );
    const viewOffsetWorld = Cartesian3.add(
      this.dragStartPositionWorld,
      normalDirection,
      new Cartesian3(),
    );
    const viewOffsetRaster =
      this._viewer.scene.cartesianToCanvasCoordinates(viewOffsetWorld);

    if (!viewOffsetRaster) {
      /**
       * This only seems to happen when a clipping box has been initialized
       * close to the camera. Simply returning doesn't affect the functionality,
       * but not returning causes cesium to crash.
       */
      console.warn("Unable to determine viewOffsetRaster.");
      return;
    }

    const screenScale = Cartesian2.distance(viewCentreRaster, viewOffsetRaster);
    this.rasterDelta = Cartesian2.multiplyByScalar(
      rasterDelta,
      screenScale,
      rasterDelta,
    );

    const aa =
      normalDirection.x > 0.9
        ? new Cartesian3(0, 1, 0)
        : new Cartesian3(1, 0, 0);
    const vv = new Cartesian3();
    Cartesian3.cross(normalDirection, aa, vv);
    const ww = new Cartesian3();
    Cartesian3.cross(normalDirection, vv, ww);

    if (this.config.debug.enabled) {
      this.clickTargetDebugPrimitive = this._viewer.scene.primitives.add(
        new PointPrimitiveCollection(),
      );
      this.clickTargetDebugPrimitive.add({
        position: viewOffsetWorld,
        ...this.config.debug.clickTarget,
      });
      this.axisDebugPrimitive = new DebugModelMatrixPrimitive({
        modelMatrix: new Matrix4(
          normalDirection.x,
          vv.x,
          ww.x,
          faceCenter.x,
          normalDirection.y,
          vv.y,
          ww.y,
          faceCenter.y,
          normalDirection.z,
          vv.z,
          ww.z,
          faceCenter.z,
          0,
          0,
          0,
          1,
        ),
        ...this.config.debug.faceAxis,
      });
      this._viewer.scene.primitives.add(this.axisDebugPrimitive);
    }
    this._viewer.scene.requestRender();
  }

  private handleMouseUp() {
    if (this.selectedPlaneEntity) {
      this.previouslySelectedPlaneEntityId = this.selectedPlaneEntity.id;
      //tricky bit: whenever setting plane color, you have to set outline color too, otherwise clipping box does not clip
      this.selectedPlaneEntity.plane.material = new ColorMaterialProperty(
        this.config.faces.color,
      );
      this.selectedPlaneEntity.plane.outlineColor = new ColorMaterialProperty(
        this.config.faces.outlineColor,
      );
      this.selectedPlaneEntity = undefined;
    }

    this.enableCameraInputsAfterDrag();

    if (this.config.debug.enabled) {
      this._viewer.scene.primitives.remove(this.axisDebugPrimitive);
      this._viewer.scene.primitives.remove(this.clickTargetDebugPrimitive);
    }
    this._viewer.scene.requestRender();
  }

  private handleMouseMove(ev: PositionEvent) {
    this._mouseMoveEvent = ev;
    if (
      this._clippingMode.getSnapshot() === "advanced" &&
      !this._isInitialized.getSnapshot()
    ) {
      if (!this._entityCursorPoint) {
        this._entityCursorPoint = this.addEntityCursorPoint();
      }

      this.requestPreviewOrientationUpdate();
      this.requestPreviewRender();
    } else {
      if (this._entityCursorPoint) {
        this.removeEntityCursorPoint();
      }

      if (!this.selectedPlaneEntity) {
        this.handleMouseHover(ev);
        return;
      }
      if (!this.selectedPlaneEntity.properties.normal) {
        console.warn('Selected entity is missing "normal" property.');
      }

      const planeIndex =
        this.selectedPlaneEntity.properties.planeIndex.getValue();

      // Point on the drag ray that is closest to the pick ray.
      const pickRay = this._viewer.camera.getPickRay(ev.screenPosition);

      // Vector from the drag start position to the camera position
      const cameraToStartVector = Cartesian3.subtract(
        this._viewer.camera.position,
        this.dragStartPositionWorld,
        new Cartesian3(),
      );

      // Multiplier for the drag vector to get the closest point on the drag ray to the pick ray.
      const closestPointScalar = VectorUtils.getRaysClosestPoint(
        this.dragVectorWorld,
        pickRay.direction,
        cameraToStartVector,
      );

      this.clippingDistances[planeIndex] =
        this.originalDistance + closestPointScalar;
      this._hasUserAdjustedBox.set(true);

      this._viewer.scene.requestRender();
    }
  }

  private handleMouseHover(ev: PositionEvent) {
    if (!this._viewer || this.selectedPlaneEntity) return;

    try {
      const hoveredPlane = this.pickClippingPlane(ev.screenPosition);

      if (
        !hoveredPlane ||
        !hoveredPlane.id ||
        !hoveredPlane?.id?.name?.includes("clipping-entity")
      ) {
        if (this.previouslyHoveredEntity) {
          this.handleMouseHoverExit();
        }
        return;
      }

      this.hoveredPlaneEntity = hoveredPlane.id;

      if (
        this.previouslyHoveredEntity !== this.hoveredPlaneEntity ||
        this.previouslySelectedPlaneEntityId === this.hoveredPlaneEntity.id
      ) {
        this.handleMouseHoverExit();

        this.hoveredPlaneEntity.plane.material = new ColorMaterialProperty(
          this.config.faces.hoveredColor,
        );
        this.hoveredPlaneEntity.plane.outlineColor = new ColorMaterialProperty(
          this.config.faces.outlineColor,
        );
        this.previouslyHoveredEntity = this.hoveredPlaneEntity;
      }
      this._viewer.scene.requestRender();
    } catch (error) {
      this.recoverSceneAfterFailedPick(this._viewer);
      console.error(error);
    }
  }

  private handleMouseHoverExit() {
    if (this.previouslyHoveredEntity) {
      this.previouslyHoveredEntity.plane.material = new ColorMaterialProperty(
        this.config.faces.color,
      );
      this.previouslyHoveredEntity.plane.outlineColor =
        new ColorMaterialProperty(this.config.faces.outlineColor);
      this.previouslyHoveredEntity = null;
    }
  }

  private requestPreviewRender() {
    this._viewer?.scene.requestRender();
  }

  private requestPreviewOrientationUpdate() {
    if (this._pendingPreviewOrientationUpdate) return;

    const elapsed = Date.now() - this._lastPreviewOrientationUpdateMs;
    const delay = Math.max(0, this._previewOrientationThrottleMs - elapsed);

    this._pendingPreviewOrientationUpdate = setTimeout(() => {
      this._pendingPreviewOrientationUpdate = null;
      this.updatePreviewOrientationFromQuickNormal();
    }, delay);
  }

  private updatePreviewOrientationFromQuickNormal() {
    const ev = this._mouseMoveEvent;
    if (
      !this._viewer ||
      !this._isActive ||
      this._clippingMode.getSnapshot() !== "advanced" ||
      this._isInitialized.getSnapshot() ||
      !ev?.position
    ) {
      return;
    }

    this._lastPreviewOrientationUpdateMs = Date.now();

    const normal = this.getAdvancedClippingBoxPreviewNormal(ev);
    if (!normal) return;

    const orientation = this.getOrientationFromSurfaceNormal(
      ev.position,
      normal,
    );
    if (!orientation) return;

    this._previewOrientation = orientation;
    this._viewer.scene.requestRender();
  }

  private clearPreviewOrientationUpdate() {
    if (this._pendingPreviewOrientationUpdate) {
      clearTimeout(this._pendingPreviewOrientationUpdate);
      this._pendingPreviewOrientationUpdate = null;
    }
    this._previewOrientation = Quaternion.IDENTITY;
    this._lastPreviewOrientationUpdateMs = 0;
  }

  private getOrientationFromSurfaceNormal(
    position: Cartesian3,
    normal: Cartesian3,
  ): Quaternion | null {
    const enuTransform = Transforms.eastNorthUpToFixedFrame(position);
    const enuNormal = Matrix4.multiplyByPointAsVector(
      Matrix4.inverseTransformation(enuTransform, new Matrix4()),
      normal,
      new Cartesian3(),
    );
    enuNormal.z = 0;
    if (Cartesian3.dot(enuNormal, enuNormal) === 0) return null;

    Cartesian3.normalize(enuNormal, enuNormal);
    const azimuth = Math.atan2(enuNormal.x, enuNormal.y);
    const azimuthDeg = CesiumMath.toDegrees(
      azimuth < 0 ? azimuth + 2 * Math.PI : azimuth,
    );

    return Transforms.headingPitchRollQuaternion(
      position,
      HeadingPitchRoll.fromDegrees(azimuthDeg, 0, 0),
    );
  }

  private addEntityCursorPoint() {
    if (!this._viewer || !this._mouseMoveEvent?.position) return;

    const viewportWidthMultiplier = 0.25;
    const viewportHeightMultiplier = 0.5;
    const closeDistanceThreshold = 30;

    const dimensions = new CallbackProperty(() => {
      if (!this._mouseMoveEvent?.position) return new Cartesian3(1, 1, 1);
      const cameraDistance = Cartesian3.distance(
        this._viewer.camera.positionWC,
        this._mouseMoveEvent.position,
      );

      let horizontal: number;
      let vertical: number;
      if (cameraDistance <= closeDistanceThreshold) {
        horizontal = (cameraDistance * viewportWidthMultiplier) / 2;
        vertical = horizontal * viewportHeightMultiplier * 1.75;
      } else {
        horizontal = cameraDistance * viewportWidthMultiplier;
        vertical = horizontal * viewportHeightMultiplier;
      }

      return new Cartesian3(horizontal * 2, horizontal * 2, vertical * 2);
    }, false);

    return this._viewer.entities.add({
      position: new CallbackPositionProperty(
        () => this._mouseMoveEvent.position,
        false,
      ),
      show: true,
      orientation: new CallbackProperty(() => this._previewOrientation, false),
      box: {
        dimensions,
        material: new ColorMaterialProperty(
          new CallbackProperty(
            () => CLIPPING_BOX_PREVIEW_COLOR.withAlpha(0.15),
            false,
          ),
        ),
        outline: true,
        outlineColor: new CallbackProperty(
          () => CLIPPING_BOX_PREVIEW_COLOR,
          false,
        ),
      },
      properties: {
        excludeFromPicking: true,
      },
    });
  }

  private removeEntityCursorPoint() {
    this.clearPreviewOrientationUpdate();
    if (!this._viewer) return;
    this._viewer.entities.remove(this._entityCursorPoint);
    this._entityCursorPoint = null;
  }

  private initializeClippingBox(
    initialRotationDegrees?: number,
    initialOrigin?: Cartesian3,
  ) {
    if (this._isInitialized.getSnapshot()) {
      console.warn(
        "initialize() called, but clipping box is already initialized",
      );
      return;
    }

    const { scene } = this._viewer;
    const { camera, canvas, globe } = this._viewer.scene;
    this.normals = this.config.faces.normals;
    this.planes = this.normals.map((normal) => new Plane(normal, 1));
    this.faceCenters = new Array(this.planes.length);
    this.influencerIndices = this.normals.map((_, i) => {
      const indices = this.normals.map((_, j) => j);
      indices.splice(i % 2 === 0 ? i : i - 1, 2);
      return indices;
    });

    this.positionIndices = this.normals.map((normal) => {
      return new Cartesian3(
        normal.x === 0 ? 1 : 0,
        normal.y === 0 ? 1 : 0,
        normal.z === 0 ? 1 : 0,
      );
    });

    this.clippingDistances = this.normals.map(() => 1);

    /**
     * Get the world coordinates at which the camera is pointed.
     */
    const viewportCenterScreenSpaceCoords = new Cartesian2(
      canvas.clientWidth / 2,
      canvas.clientHeight / 2,
    );
    const objectsToExclude = this._viewer.entities.values?.filter(
      (e) => e.properties?.excludeFromPicking,
    );
    const viewportCenterRay = camera.getPickRay(
      viewportCenterScreenSpaceCoords,
    );

    if (!viewportCenterRay) {
      console.warn("unable to get pick ray for viewport center");
      return;
    }

    const pickedFromRay = scene.pickFromRay(
      viewportCenterRay,
      objectsToExclude,
      0.005,
    );

    const position30MetersInFrontOfCamera = Cartesian3.add(
      camera.positionWC,
      Cartesian3.multiplyByScalar(camera.directionWC, 30, new Cartesian3()),
      new Cartesian3(),
    );

    /**
     * pickFromRay() will return a position if it intersects with a tileset.
     * Otherwise, use the position at which the ray intersects the terrain,
     * falling back to the position 30 meters in front of the camera if the
     * it's is pointed above the horizon.
     */
    const viewportCenterWorldPosition =
      pickedFromRay?.position ??
      globe.pick(viewportCenterRay, scene) ??
      position30MetersInFrontOfCamera;

    /**
     * These default multipliers will create a clipping box
     * spanning half the viewport width horizontally, and
     * a quarter of the viewport width vertically.
     *
     * The viewport width is used to set the vertical dimensions
     * of the clipping box instead of the viewport height
     * because the vertical distance covered by the viewport
     * increases dramatically as the camera angle approaches
     * the horizon due to perspective projection.
     */
    const viewportWidthMultiplier = 0.25;
    const viewportHeightMultiplier = 0.5;
    let clippingDistanceHorizontal: number;
    let clippingDistanceVertical: number;

    if (this._viewer.scene.cameraUnderground) {
      /**
       * Measuring the distance covered by the viewport horizontally
       * is inconsistent when the camera is underground, so the
       * clipping box dimensions are explicitly defined in this case.
       */
      clippingDistanceHorizontal = 15;
      clippingDistanceVertical =
        clippingDistanceHorizontal * viewportHeightMultiplier;
      // this.boxOrigin = viewportCenterWorldPosition;

      if (initialOrigin) {
        this.boxOrigin = initialOrigin;
      } else if (viewportCenterWorldPosition) {
        /**
         * Set the clipping box origin to the point on the terrain or tileset
         * at which the camera is pointed.
         */
        this.boxOrigin = viewportCenterWorldPosition;
      } else {
        /**
         * If that position can't be determined, set the clipping box origin to a point
         * 30 meters in front of the camera.
         */
        this.boxOrigin = position30MetersInFrontOfCamera;
      }
    } else {
      /**
       * If the camera is pointed at a tileset, use the distance
       * between the camera and the tileset to determine the
       * clipping box dimensions. If the camera is within a
       * certain distance threshold to the tileset, relax
       * the vertical constraints on the clipping box dimensions.
       * Set the clipping box origin to the point on the tileset
       * at which the camera is pointed.
       */
      const cameraToTilesetDistanceThreshold = 30;
      const cameraToTilesetDistance = Cartesian3.distance(
        camera.positionWC,
        initialOrigin ?? viewportCenterWorldPosition,
      );

      clippingDistanceHorizontal =
        cameraToTilesetDistance <= cameraToTilesetDistanceThreshold
          ? (cameraToTilesetDistance * viewportWidthMultiplier) / 2
          : cameraToTilesetDistance * viewportWidthMultiplier;
      clippingDistanceVertical =
        cameraToTilesetDistance <= cameraToTilesetDistanceThreshold
          ? clippingDistanceHorizontal * viewportHeightMultiplier * 1.75
          : clippingDistanceHorizontal * viewportHeightMultiplier;

      this.boxOrigin = initialOrigin ?? viewportCenterWorldPosition;
    }

    this.initialClippingDistance = clippingDistanceHorizontal;

    this.boxCenter = this.boxOrigin;

    this.clippingDistances = [
      clippingDistanceHorizontal,
      clippingDistanceHorizontal,
      clippingDistanceHorizontal,
      clippingDistanceHorizontal,
      clippingDistanceVertical,
      clippingDistanceVertical,
    ];

    // Set the rotation of the clipping box to the camera heading.
    this.rotationDegrees =
      initialRotationDegrees ??
      CesiumMath.toDegrees(this._viewer.camera.heading);

    this.setEnuRotation();
    this._isInitialized.set(true);
    this.removeEntityCursorPoint();

    this.setTilesets();
    this._viewer.scene.requestRender();
  }

  private setEnuRotation() {
    const boxCenterLocal = new Cartesian3(
      (this.clippingDistances[1] - this.clippingDistances[0]) * 0.5,
      (this.clippingDistances[3] - this.clippingDistances[2]) * 0.5,
      (this.clippingDistances[5] - this.clippingDistances[4]) * 0.5,
    );

    this.enuRotation = Matrix4.multiplyByMatrix3(
      Transforms.eastNorthUpToFixedFrame(this.boxCenter),
      Matrix3.fromRotationZ(CesiumMath.toRadians(-this.rotationDegrees)),
      this.enuRotationScratchMatrix,
    );

    this.enuRotation = Matrix4.multiplyByTranslation(
      this.enuRotation,
      boxCenterLocal,
      this.enuRotation,
    );
    this._viewer.scene.requestRender();
  }

  activateTilesetClipping(tileset: Cesium3DTileset) {
    if (!tileset?.clippingPlanes) return;
    const tilesetId = this.getTilesetId(tileset);
    if (tilesetId) {
      this._recalledActiveTilesetIds?.add(tilesetId);
      this._recalledDeactivatedTilesetIds.delete(tilesetId);
    }
    tileset.clippingPlanes.enabled = true;
    this.deactivatedTilesets = this.deactivatedTilesets.filter(
      (t) => t !== tileset,
    );
    this._clippedTilesets.set(
      this._clippedTilesets
        .getSnapshot()
        .map((ct) =>
          ct.tileset === tileset ? { ...ct, isClippingEnabled: true } : ct,
        ),
    );
    this._hasUserAdjustedBox.set(true);
    this._viewer.scene.requestRender();
  }

  deactivateTilesetClipping(tileset: Cesium3DTileset) {
    const tilesetId = this.getTilesetId(tileset);
    if (tilesetId) {
      this._recalledActiveTilesetIds?.add(tilesetId);
      this._recalledDeactivatedTilesetIds.add(tilesetId);
    }
    tileset.clippingPlanes.enabled = false;
    this.deactivatedTilesets = [...this.deactivatedTilesets, tileset];
    this._clippedTilesets.set(
      this._clippedTilesets
        .getSnapshot()
        .map((ct) =>
          ct.tileset === tileset ? { ...ct, isClippingEnabled: false } : ct,
        ),
    );
    this._hasUserAdjustedBox.set(true);
    this._viewer.scene.requestRender();
  }

  createUpdateClippingPlaneFn(planeIndex: number) {
    return () => {
      const clippedTilesets = this._clippedTilesets.getSnapshot();
      for (let i = 0; i < clippedTilesets.length; i++) {
        const clippingPlanes = clippedTilesets[i].tileset.clippingPlanes;
        const clippingPlane = clippingPlanes?.get(planeIndex);
        if (!clippingPlane) continue;

        clippingPlanes.modelMatrix = Matrix4.multiply(
          this.initialModelMatrices[i],
          this.enuRotation,
          this.scratchModelMatrices[i],
        );
        clippingPlane.distance = this.clippingDistances[planeIndex];
      }

      this.planes[planeIndex].distance = this.clippingDistances[planeIndex];

      this.planeEntities[planeIndex].plane.dimensions = new ConstantProperty(
        new Cartesian2(
          this.clippingDistances[this.influencerIndices[planeIndex][0]] +
            this.clippingDistances[this.influencerIndices[planeIndex][1]],
          this.clippingDistances[this.influencerIndices[planeIndex][2]] +
            this.clippingDistances[this.influencerIndices[planeIndex][3]],
        ),
      );

      this.calculatePlanePositions(planeIndex);

      return this.planes[planeIndex];
    };
  }

  setRotation(degrees: number) {
    if (!this._isInitialized.getSnapshot() || !this.clippingDistances) return;

    if (isNaN(degrees)) {
      console.warn("NaN passed to ClippingBox.setRotation()");
      degrees = 0;
    }
    this.rotationDegrees = degrees;
    this.setEnuRotation();
    this._hasUserAdjustedBox.set(true);
  }

  setIsSelectingBackface(val: boolean) {
    this.isSelectingBackface = val;
  }

  setTilesets() {
    if (!this._isInitialized.getSnapshot()) return;

    const tilesets = this._tilesetPlugin
      .getAll()
      .filter(({ tileset }) => !!tileset && tileset.show)
      .map(({ tileset }) => tileset);

    this.deactivatedTilesets = tilesets.filter(
      (tileset) => !this.shouldEnableTilesetFromRecalledState(tileset),
    );
    this._clippedTilesets.set(
      tilesets.map((tileset) => this.toClippedTileset(tileset)),
    );
    const enu = Transforms.eastNorthUpToFixedFrame(this.boxOrigin);

    for (let i = 0; i < tilesets.length; i++) {
      const modelReferenceMatrix = Matrix4.inverse(
        tilesets[i].clippingPlanesOriginMatrix,
        new Matrix4(),
      );
      const modelMatrix = Matrix4.multiply(
        modelReferenceMatrix,
        enu,
        new Matrix4(),
      );
      tilesets[i].clippingPlanes = new ClippingPlaneCollection({
        modelMatrix,
        unionClippingRegions: true,
        edgeColor: this.config.clippedEdges.color,
        edgeWidth: this.config.clippedEdges.width,
        enabled: this.shouldEnableTilesetFromRecalledState(tilesets[i]),
        planes: this.planes.map(
          (plane) => new ClippingPlane(Cartesian3.clone(plane.normal), 1),
        ),
      });
      this.initialModelMatrices[i] = modelReferenceMatrix;
      this.scratchModelMatrices[i] = new Matrix4();
    }

    this.planeEntities = this.planes.map((plane, i) => {
      const planeEntity = this._viewer.entities.add({
        name: `clipping-entity-${i}`,
        show: this._isActive,
        properties: {
          normal: plane.normal,
          planeIndex: i,
          // excludeFromPicking: true,
        },
        position: this.boxOrigin,
        plane: {
          plane: new CallbackProperty(
            this.createUpdateClippingPlaneFn(i),
            false,
          ),
          material: new ColorMaterialProperty(this.config.faces.color),
          dimensions: new ConstantProperty(
            new Cartesian2(
              this.initialClippingDistance,
              this.initialClippingDistance,
            ),
          ), // plane.dimensions gets overridden almost immediately in the update loop
          outline: true,
          outlineColor: new ColorMaterialProperty(
            this.config.faces.outlineColor,
          ),
        },
      });

      if (this.config.debug.enabled) {
        this.faceCenterDebugPointPrimitives.add({
          position: new Cartesian3(),
          ...this.config.debug.faceCenter,
        });
      }

      return planeEntity;
    });

    this.planeScratchCartesian3Instances = this.planes.map(() =>
      Array.from({ length: 6 }, () => new Cartesian3()),
    );
    this.planeScratchHprInstances = Array.from(
      { length: 6 },
      () => new HeadingPitchRoll(),
    );
    this.orientationTransformScratchMatrix3Instances = Array.from(
      { length: 6 },
      () => new Matrix3(),
    );
    this.planeFaceCenterScratchCartesian3Instances = this.planes.map(() =>
      Array.from({ length: 3 }, () => new Cartesian3()),
    );

    this.planeEntities.forEach((_, i) => {
      this.calculatePlanePositions(i);
    });
  }

  private scheduleSyncTilesets() {
    if (this._syncTimeout) clearTimeout(this._syncTimeout);
    this._syncTimeout = setTimeout(() => {
      this._syncTimeout = null;
      this.syncTilesets();
    }, 0);
  }

  private clearSyncTimeout() {
    if (this._syncTimeout) {
      clearTimeout(this._syncTimeout);
      this._syncTimeout = null;
    }
  }

  /**
   * Incrementally synchronize the clipped tilesets list with
   * the currently visible tilesets, without recreating plane entities.
   * Newly visible tilesets get clipping planes; hidden tilesets are removed.
   */
  private syncTilesets() {
    if (!this._isInitialized.getSnapshot() || !this._viewer) return;

    const tilesets = this._tilesetPlugin
      .getAll()
      .filter(({ tileset }) => !!tileset && tileset.show)
      .map(({ tileset }) => tileset);

    const previousClippedTilesets = this._clippedTilesets.getSnapshot();
    const previousTilesetMap = new Map(
      previousClippedTilesets.map((ct) => [ct.tileset, ct]),
    );
    const currentTilesetSet = new Set(tilesets);

    if (
      previousClippedTilesets.length === tilesets.length &&
      previousClippedTilesets.every((ct) => currentTilesetSet.has(ct.tileset))
    ) {
      return;
    }

    for (const ct of previousClippedTilesets) {
      if (!currentTilesetSet.has(ct.tileset) && ct.tileset.clippingPlanes) {
        ct.tileset.clippingPlanes.enabled = false;
        ct.tileset.clippingPlanes.removeAll();
      }
    }

    this.deactivatedTilesets = this.deactivatedTilesets.filter((t) =>
      currentTilesetSet.has(t),
    );

    const enu = Transforms.eastNorthUpToFixedFrame(this.boxOrigin);
    const newClippedTilesets: ClippedTileset[] = [];
    this.initialModelMatrices = [];
    this.scratchModelMatrices = [];

    for (let i = 0; i < tilesets.length; i++) {
      const tileset = tilesets[i];
      const previous = previousTilesetMap.get(tileset);

      const modelReferenceMatrix = Matrix4.inverse(
        tileset.clippingPlanesOriginMatrix,
        new Matrix4(),
      );

      if (previous) {
        newClippedTilesets.push(previous);
      } else {
        const modelMatrix = Matrix4.multiply(
          modelReferenceMatrix,
          enu,
          new Matrix4(),
        );
        tileset.clippingPlanes = new ClippingPlaneCollection({
          modelMatrix,
          unionClippingRegions: true,
          edgeColor: this.config.clippedEdges.color,
          edgeWidth: this.config.clippedEdges.width,
          enabled: this.shouldEnableTilesetFromRecalledState(tileset),
          planes: this.planes.map(
            (plane) => new ClippingPlane(Cartesian3.clone(plane.normal), 1),
          ),
        });
        newClippedTilesets.push(this.toClippedTileset(tileset));
      }

      this.initialModelMatrices[i] = modelReferenceMatrix;
      this.scratchModelMatrices[i] = new Matrix4();
    }

    this._clippedTilesets.set(newClippedTilesets);
    this.deactivatedTilesets = newClippedTilesets
      .filter((ct) => !ct.isClippingEnabled)
      .map((ct) => ct.tileset);
    this._viewer.scene.requestRender();
  }

  private calculatePlanePositions(planeIndex: number) {
    const boxCenterLocal = new Cartesian3(
      (this.clippingDistances[1] - this.clippingDistances[0]) * 0.5,
      (this.clippingDistances[3] - this.clippingDistances[2]) * 0.5,
      (this.clippingDistances[5] - this.clippingDistances[4]) * 0.5,
    );
    const boxCenterLocalOffset = Cartesian3.dot(
      this.normals[planeIndex],
      boxCenterLocal,
    );

    const faceCenterLocalOffset = Cartesian3.subtract(
      Cartesian3.multiplyByScalar(
        this.normals[planeIndex],
        boxCenterLocalOffset,
        this.planeScratchCartesian3Instances[planeIndex][0],
      ),
      boxCenterLocal,
      this.planeScratchCartesian3Instances[planeIndex][1],
    );

    const planePosition = Matrix4.multiplyByPoint(
      this.enuRotation,
      faceCenterLocalOffset,
      this.planeScratchCartesian3Instances[planeIndex][2],
    );

    this.planeEntities[planeIndex].position = new ConstantPositionProperty(
      planePosition,
    );

    const planeOrientation = Transforms.headingPitchRollQuaternion(
      this.planeEntities[planeIndex].position.getValue(JulianDate.now()),
      HeadingPitchRoll.fromDegrees(
        this.rotationDegrees,
        0,
        0,
        this.planeScratchHprInstances[planeIndex],
      ),
    );
    this.planeEntities[planeIndex].orientation = new ConstantProperty(
      planeOrientation,
    );

    const orientationTransform = Matrix3.fromQuaternion(
      planeOrientation,
      this.orientationTransformScratchMatrix3Instances[planeIndex],
    );

    const faceCenter = Cartesian3.add(
      planePosition,
      Cartesian3.multiplyByScalar(
        Matrix3.multiplyByVector(
          orientationTransform,
          this.planes[planeIndex].normal,
          this.planeFaceCenterScratchCartesian3Instances[planeIndex][0],
        ),
        -this.planes[planeIndex].distance,
        this.planeFaceCenterScratchCartesian3Instances[planeIndex][1],
      ),
      this.planeFaceCenterScratchCartesian3Instances[planeIndex][2],
    );

    this.faceCenters[planeIndex] = faceCenter;
    if (this.faceCenters.every(Boolean)) {
      this.boxCenter = PointsUtils.getCentroid(this.faceCenters);
    }
  }

  private setPlaneEntitiesVisible(visible: boolean) {
    if (!this.planeEntities) return;
    for (const entity of this.planeEntities) {
      entity.show = visible;
    }
    this._viewer?.scene.requestRender();
  }

  removeEntities() {
    for (let i = 0; i < this.planeEntities?.length; i++) {
      this._viewer.entities.remove(this.planeEntities[i]);
    }

    if (this.config.debug.enabled) {
      this._viewer.scene.primitives.remove(this.clickTargetDebugPrimitive);
      this._viewer.scene.primitives.remove(this.boxCenterDebugPointPrimitives);
      this._viewer.scene.primitives.remove(this.axisDebugPrimitive);
      this._viewer.scene.primitives.remove(this.faceCenterDebugPointPrimitives);
      this._viewer.scene.primitives.remove(this.boxOriginDebugPointPrimitives);
    }
    this.removeEntityCursorPoint();
  }

  destroyClippingBox() {
    if (!this._viewer) return;
    this.clearSyncTimeout();
    this.removeEntities();

    this.clippedTilesets.getSnapshot()?.forEach((clippedTileset) => {
      clippedTileset.tileset.clippingPlanes.enabled = false;
      clippedTileset.tileset.clippingPlanes.removeAll();
    });

    this._clippedTilesets.set([]);
    this._recalledActiveTilesetIds = null;
    this._recalledDeactivatedTilesetIds.clear();
    this.normals = null;
    this.planes = null;
    this.influencerIndices = null;
    this.positionIndices = null;
    this.clippingDistances = null;
    this.faceCenters = null;
    this.rotationDegrees = 0;
    this.isSelectingBackface = false;
    this.isIsolatingFace = false;
    this.planeEntities = null;
    this._hasUserAdjustedBox.set(false);
    this._isInitialized.set(false);
    this._viewer.scene.requestRender();
  }
}
